// shared/numeric/numeric-stepper.component.ts

import { ChangeDetectionStrategy, Component, OnDestroy, input } from '@angular/core';

import type { NumericMaskDirective } from './numeric-mask.directive';

/**
 * Replacement for the native `<input type="number">` spinner, which is unavailable
 * because masked inputs must stay `type="text"`.
 *
 * Never placed in a template by hand — `NumericMaskDirective` creates it and injects
 * it next to the input it decorates.
 */
@Component({
    selector: 'app-numeric-stepper',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <button
            type="button"
            tabindex="-1"
            aria-hidden="true"
            [disabled]="for().disabled()"
            (mousedown)="press($event, 1)"
            (mouseup)="release()"
            (mouseleave)="release()">
            <span class="arrow arrow--up"></span>
        </button>
        <button
            type="button"
            tabindex="-1"
            aria-hidden="true"
            [disabled]="for().disabled()"
            (mousedown)="press($event, -1)"
            (mouseup)="release()"
            (mouseleave)="release()">
            <span class="arrow arrow--down"></span>
        </button>
    `,
    styles: [`
        :host {
            position: absolute;
            inset-inline-end: 0;
            top: 50%;
            transform: translateY(-50%);
            display: flex;
            flex-direction: column;
            opacity: 0;
            pointer-events: none;
            transition: opacity 120ms ease-in-out;
        }

        /* Only rendered while the input is focused, so unfocused fields keep no reserved space. */
        :host(.numeric-stepper--visible) {
            opacity: 1;
            pointer-events: auto;
        }

        button {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 16px;
            height: 11px;
            padding: 0;
            border: none;
            background: transparent;
            cursor: pointer;
            color: currentColor;
            opacity: 0.65;
        }

        button:hover:not(:disabled) { opacity: 1; }

        /* Overrides the global disabled-button min-height rule in styles.scss. */
        :host button:disabled {
            opacity: 0.3;
            cursor: default;
            min-height: 0 !important;
        }

        .arrow {
            width: 0;
            height: 0;
            border-inline: 4px solid transparent;
        }

        .arrow--up { border-block-end: 5px solid currentColor; }
        .arrow--down { border-block-start: 5px solid currentColor; }
    `],
})
export class NumericStepperComponent implements OnDestroy {
    readonly for = input.required<NumericMaskDirective>();

    private holdTimer: ReturnType<typeof setTimeout> | null = null;
    private repeatTimer: ReturnType<typeof setInterval> | null = null;

    press(event: MouseEvent, direction: 1 | -1): void {
        if (event.button !== 0) return;
        // Keep focus (and the caret) on the input instead of moving it to the button.
        event.preventDefault();

        this.for().focusInput();
        this.step(direction);

        // Auto-repeat on hold, matching the native spinner.
        this.holdTimer = setTimeout(() => {
            this.repeatTimer = setInterval(() => this.step(direction), 60);
        }, 400);
    }

    release(): void {
        if (this.holdTimer !== null) {
            clearTimeout(this.holdTimer);
            this.holdTimer = null;
        }
        if (this.repeatTimer !== null) {
            clearInterval(this.repeatTimer);
            this.repeatTimer = null;
        }
    }

    ngOnDestroy(): void {
        this.release();
    }

    private step(direction: 1 | -1): void {
        const target = this.for();
        if (direction === 1) target.stepUp();
        else target.stepDown();
    }
}
